import { Field, useFormikContext } from 'formik';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  Clipboard,
  Copy,
  ExclamationCircleFill,
  InfoCircle,
  InfoCircleFill,
} from 'react-bootstrap-icons';
import toast from 'react-hot-toast';
import img404 from '../../../../assets/Images/404.jpg';
import noMessageIcon from '../../../../assets/Images/no_copied_image.jpg';
import { useStore } from '../../../../contexts/store-provider';
import {
  getTenantAccessFromLocalStorage,
  getTenantIdFromLocalStorage,
} from '../../../../helpers/localstorage-helper';
import {
  collectMatchingDeletedFiles,
  isSameProductFileReference,
  isTrue,
  mergeUniqueProductFiles,
} from '../../../../helpers/product-file-delete-helper';
import { IActionFormatter } from '../../../../models/ICommon';
import {
  IFileIndices,
  IProductFiles,
  IProductImages,
} from '../../../../models/response/IProductResponse';
import DragDropFileInput from '../../../../shared-components/drag-drop-image-upload/drag-drop-image-uploader';
import FormikFormSearchSelectField from '../../../../shared-components/FormikFormHandlers/FormikFormSearchSelectField';
import { formatMessage } from '../../../../translations/format-message';
import ImageTabView from './image-content-tab-view.tsx/image-tab-view';
import {
  ExportIcon,
  FileCaptionIcon,
  PlusIcon,
  DeleteIcon,
} from '../../../../shared-components/svgs/svgs';
import { is } from 'date-fns/locale';
import DimensionTableView from './image-content-tab-view.tsx/image-content-types/dimension-table-view';
import {
  extractSelectedColumnData,
  filterTableDataBySelected,
} from '../../../../helpers/column-utils';
import ConfirmDialog from '../confirm-dialog';
// import ConfirmDialog from '../confirm-dialog';

interface IProps {
  id: number;
  selectedProcessId: number;
  setSelectedProcessId: (processId: number) => void;
}

const ProcessTabContent = observer((props: IProps) => {
  const HEADING_MAX_LENGTH = 256;
  const { values, isValid, handleSubmit, setFieldValue, errors, resetForm } =
    useFormikContext<any>();

  //#region Local States/Variables
  const [isCopyPanelOpen, setIsCopyPanelOpen] = useState(false);
  const { id, selectedProcessId, setSelectedProcessId } = props;
  const { productStore, teplarPrintStore } = useStore();
  const {
    DeleteProductFileService,
    allProductImages,
    UploadProductFileService,
    UploadFileCopyService,
    allProductSearchSelectOptions,
    getCopiedImagesUrl,
    setCopiedImagesUrl,
    GetAllProductService,
    GetProductImagesByIdService,
    addProductFileState,
    getDimensionByProcessId,
    setNewDeletedFiles,
    setOldDeletedFiles,
  } = productStore;

  const activeIndex = Number(values?.activeIndex || 1);
  const setActiveIndex = (val: number) => setFieldValue('activeIndex', val);
  const selectedImageIndex = Number(values?.activeIndex || 1);
  const setSelectedImageIndex = (val: number) =>
    setFieldValue('activeIndex', val);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState<number>(0);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [preview, setPreview] = useState(null);
  const [isEnable, setIsEnable] = useState<boolean>(false);
  const [heading, setHeading] = useState<string>(() => {
    const process = values.Process?.find((p: any) => p.ProcessId === selectedProcessId);
    const tab = process?.fileIndices?.find(
      (fi: any) => Number(fi.TabIndex) === Number(values?.activeIndex || 1)
    );
    return tab?.FileDetail?.[0]?.Heading ?? '';
  });
  const headingInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [selectedImage, setSelectedImage] = useState<IProductFiles[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingDeleteData, setPendingDeleteData] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentProcess = useMemo(
    () => values.Process?.find((p: any) => p.ProcessId === selectedProcessId),
    [values.Process, selectedProcessId]
  );

  const currentTab = useMemo(
    () =>
      currentProcess?.fileIndices?.find(
        (fi: any) => Number(fi.TabIndex) === Number(activeIndex)
      ),
    [currentProcess, activeIndex]
  );

  const [isDimensionTable, setIsDimensionTable] = useState<boolean>(
    (currentTab?.IsDimensionTable as boolean) || false
  );

  const selectedProcessIndex = values.Process?.findIndex(
    (p: any) => p.ProcessId === selectedProcessId
  );
  const imageTabLimit = 5;
  const imageTabCount =
    currentProcess?.fileIndices?.filter(
      (fi: any) => !fi?.IsDimension && !fi?.IsDefault && !fi?.isDefault
    )?.length ?? 0;
  const isImageTabLimitReached = imageTabCount >= imageTabLimit;
  const allTabCount = currentProcess?.fileIndices?.length ?? 0;
  //#endregion Local States/Variables

  //#region Hooks
  useEffect(() => {
    setPreview(null);
    setFile(null);
    setFieldValue('file', null);
    setPreviewImageIndex(0);

   const hasExistingActiveTab = Boolean(currentTab);
    if (hasExistingActiveTab) {
      const currentHeading = currentTab?.FileDetail?.[0]?.Heading;
      setHeading(currentHeading ?? '');
      if (headingInputRef.current) {
        headingInputRef.current.value = currentHeading ?? '';
      }
    } else {
      setHeading('');
      if (headingInputRef.current) {
        headingInputRef.current.value = '';
      }
    }
  }, [activeIndex, selectedProcessId, currentTab, setFieldValue]);

  const tableData = values?.Dimension?.TableData || [];
  const lastRowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (lastRowRef.current && tableData?.length > 0) {
      lastRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [tableData?.length]);

  useEffect(() => {
    const process = values.Process?.find(
      (p: any) => p.ProcessId === selectedProcessId
    );
    if (process && process.fileIndices?.length > 0) {
      const indices = process.fileIndices;

      // Priority Logic:
      // 1. Common (IsDefault)
      // 2. Dimension (IsDimension)
      // 3. Others (Added in Step 4)

      let targetIndex = -1;

      const isTrue = (val: any) =>
        val === true || String(val).toLowerCase() === 'true';

      // Try to find first Common (IsDefault)
      const commonTab = indices.find(
        (i: any) => isTrue(i.IsDefault) || isTrue(i.isDefault)
      );

      if (commonTab) {
        targetIndex = commonTab.TabIndex;
      } else {
        // Fallback to Dimension
        const dimensionTab = indices.find(
          (i: any) => isTrue(i.IsDimension) || isTrue(i.isDimension)
        );
        if (dimensionTab) {
          targetIndex = dimensionTab.TabIndex;
        } else {
          // Fallback to first available
          targetIndex = indices[0]?.TabIndex;
        }
      }

      if (targetIndex !== -1) {
        setActiveIndex(targetIndex);
      }
    }
    setIsCopyPanelOpen(false);
    setIsFullWidth(true)
    setCopiedImagesUrl([]);
    setFieldValue('SelectedCopyProductImage', -1);
  }, [selectedProcessId]);

  useEffect(() => {
    setIsEnable(Boolean(heading.trim()) && Boolean(file));
  }, [heading, file]);

  useEffect(() => {
    if (isCopyPanelOpen) {
      GetAllProductService(Number(getTenantIdFromLocalStorage()));
    }
  }, [isCopyPanelOpen, GetAllProductService]);
  //#endregion Hooks

  //#region Local Functions
  const handleClick = (
    index: number,
    productImage?: IProductFiles[],
    isDimensionTable?: boolean
  ) => {
    if (productImage?.length > 0) {
      setSelectedImageIndex(index);
      setSelectedImage(productImage);
      setCopiedImagesUrl([]);
      setFieldValue('SelectedCopyProductImage', -1);
      setIsCopyPanelOpen(false)
      setActiveIndex(index);
      setSelectedImage([]);
    } else {
      setActiveIndex(-1);
      setSelectedImage([]);
    }

    if (currentProcess?.fileIndices?.length >= index) {
      let newHeading = currentProcess.fileIndices.find(
        (fi: any) => Number(fi.TabIndex) === Number(index)
      )?.FileDetail[0]?.Heading;
      setHeading(newHeading ?? '');
    }
    setActiveIndex(index);
    setIsCopyPanelOpen(false);
    setIsFullWidth(true)
  };
  const handleAddNewTab = () => {
    if (isImageTabLimitReached) return;

    const nextIndex =
      (values.Process?.find(
        (p: any) => p.ProcessId === selectedProcessId
      )?.fileIndices?.reduce(
        (max: number, fi: any) => Math.max(max, fi?.TabIndex || 0),
        0
      ) ?? 0) + 1;

    setActiveIndex(-1);

    setSelectedImage([]);
    setHeading('');
    if (headingInputRef.current) {
      headingInputRef.current.value = '';
    }
    setPreview(null);
    setFile(null);
    setFieldValue('file', null);
    setActiveIndex(nextIndex);
    setCopiedImagesUrl([]);
    setFieldValue('SelectedCopyProductImage', -1);
    setIsCopyPanelOpen(false);
    setIsFullWidth(true)
  };
  const removeTabAndFocus = (
    selectedProcessIndex: number,
    deletedTabIndex: number
  ) => {
    const currentIndices =
      values.Process?.[selectedProcessIndex]?.fileIndices ?? [];
    const updatedIndices = currentIndices
      .filter((fi: any) => fi.TabIndex !== deletedTabIndex)
      .map((fi: any, idx: number) => ({
        ...fi,
        TabIndex: idx + 1,
      }));

    setFieldValue(
      `Process.${selectedProcessIndex}.fileIndices`,
      updatedIndices
    );

    if (updatedIndices.length === 0) {
      setActiveIndex(-1);
      return;
    }

    const nonDimensionTabs = updatedIndices.filter(
      (fi: any) => !fi.IsDimension
    );
    const nonCommonTabs = nonDimensionTabs.filter(
      (fi: any) => !fi.IsDefault && !fi.isDefault
    );
    const candidates =
      nonCommonTabs.length > 0 ? nonCommonTabs : nonDimensionTabs;
    const sorted = [...candidates].sort(
      (a: any, b: any) => a.TabIndex - b.TabIndex
    );
    const nextTab =
      sorted.find((fi: any) => fi.TabIndex >= deletedTabIndex) ||
      sorted[sorted.length - 1] ||
      updatedIndices[0];

    const nextIndex = nextTab?.TabIndex ?? -1;
    setActiveIndex(nextIndex);
  };

  const handlePaste = (setFieldValue: any, selectedImage: any) => {
    setFieldValue('file', null);
    setPreview(selectedImage.S3FilePath);
    setIsEnable(true);
  };

  const handleCopy = (productObject: IProductImages) => {
    setIsCopyPanelOpen(() => true);
  };

  // Safe error message extraction
  const getErrorMessage = (error: any): string => {
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object' && error.message)
      return error.message;
    return String(error);
  };

  const getTabLockedType = useCallback(() => {
    if (!currentProcess?.fileIndices) return null;

    if (!currentTab?.FileDetail) return null;

    for (const file of currentTab.FileDetail) {
      if (file.FileType === 'application/pdf') return 'pdf';
      if (
        file.FileType?.startsWith('image/') ||
        (file.S3FilePath && !file.S3FilePath.toLowerCase().endsWith('.pdf'))
      )
        return 'image';
    }
    return null;
  }, [values.Process, selectedProcessId, activeIndex]);

  const hasCheckItemErrors = (checkItems: any[]) => {
    if (!checkItems || !Array.isArray(checkItems)) return false;
    for (const item of checkItems) {
      const isEmpty = !item.CheckMessage || item.CheckMessage.trim() === '';
      const isTouched = !!item.IsTouched;
      if (isTouched && isEmpty) return true;
    }
    return false;
  };

  const hasDuplicateItemInSameHeader = (checkItems: any[]) => {
    if (!checkItems || !Array.isArray(checkItems)) return false;

    const itemsByHeader: { [key: string]: Set<string> } = {};
    for (const item of checkItems) {
      const messages =
        item?.CheckItems && Array.isArray(item.CheckItems)
          ? item.CheckItems
          : item?.CheckItemMessage && Array.isArray(item.CheckItemMessage)
            ? item.CheckItemMessage
            : [item];
      const header = String(item?.Title || item?.HeaderTitle || '').trim();

      if (!itemsByHeader[header]) {
        itemsByHeader[header] = new Set();
      }

      for (const msg of messages) {
        const text = String(
          msg?.CheckMessage ?? msg?.text ?? msg?.CheckItemMessage ?? ''
        )
          .trim()
          .toLowerCase();

        if (!text) continue;
        if (itemsByHeader[header].has(text)) return true;
        itemsByHeader[header].add(text);
      }
    }

    return false;
  };

  const hasHeaderWithoutItemErrorForTab = (tabIndex: number) => {
    const processId = Number(selectedProcessId);
    const currentTabIndex = Number(tabIndex);
    if (processId <= 0 || currentTabIndex <= 0) return false;
    const validationKey = `${processId}_${currentTabIndex}`;
    return Boolean(values?.CheckItemHeaderValidationMap?.[validationKey]);
  };
  const hasDuplicateHeaderNameErrorForTab = (tabIndex: number) => {
    const processId = Number(selectedProcessId);
    const currentTabIndex = Number(tabIndex);
    if (processId <= 0 || currentTabIndex <= 0) return false;
    const validationKey = `${processId}_${currentTabIndex}`;
    return Boolean(
      values?.CheckItemDuplicateHeaderValidationMap?.[validationKey]
    );
  };

  const hasDuplicateHeadingInStep4ImageTabs = useCallback(
    (value: string) => {
      const normalized = String(value || '')
        .trim()
        .toLowerCase();
      if (!normalized) return false;

      const isTrue = (val: any) =>
        val === true || String(val).toLowerCase() === 'true';

      const isCommonActive = isTrue(currentTab?.IsDefault) || isTrue(currentTab?.isDefault);
      const isDimensionActive = isTrue(currentTab?.IsDimension) || isTrue(currentTab?.isDimension);
      if (isCommonActive || isDimensionActive) return false;

      // Step 4 scope only: validate against non-common, non-dimension image tabs.
      return (
        currentProcess?.fileIndices?.some((fi: any) => {
          const isCommon = isTrue(fi?.IsDefault) || isTrue(fi?.isDefault);
          const isDimension =
            isTrue(fi?.IsDimension) || isTrue(fi?.isDimension);
          if (isCommon || isDimension) return false;
          if (Number(fi?.TabIndex) === Number(activeIndex)) return false;

          return (
            String(fi?.FileDetail?.[0]?.Heading || '')
              .trim()
              .toLowerCase() === normalized
          );
        }) ?? false
      );
    },
    [currentProcess?.fileIndices, activeIndex, currentTab]
  );

  const isDuplicateHeading = useMemo(() => {
    return hasDuplicateHeadingInStep4ImageTabs(heading);
  }, [heading, hasDuplicateHeadingInStep4ImageTabs]);

  // True when the active tab already has a saved file but the heading has been cleared
  const isHeadingEmptyWithFile = useMemo(() => {
    const isCommon = isTrue(currentTab?.IsDefault) || isTrue(currentTab?.isDefault);
    const isDimension = isTrue(currentTab?.IsDimension) || isTrue(currentTab?.isDimension);
    if (isCommon || isDimension) return false;
    const hasFile = getTabLockedType() !== null;
    return hasFile && String(heading || '').trim() === '';
  }, [heading, getTabLockedType, currentTab]);

  const updateActiveProcessImageTabHeading = (value: string) => {
    if (selectedProcessIndex < 0) return;

    const currentFileIndices =
      values.Process?.[selectedProcessIndex]?.fileIndices ?? [];
    const selectedFileIndex = currentFileIndices.findIndex(
      (file: any) => Number(file?.TabIndex) === Number(activeIndex)
    );
    if (selectedFileIndex < 0) return;

    const activeTab = currentFileIndices[selectedFileIndex];
    if (
      isTrue(activeTab?.IsDefault) ||
      isTrue(activeTab?.isDefault) ||
      isTrue(activeTab?.IsDimension) ||
      isTrue(activeTab?.isDimension)
    ) {
      return;
    }

    const updatedFileIndices = currentFileIndices.map((file: any) => {
      if (Number(file?.TabIndex) !== Number(activeIndex)) return file;

      return {
        ...file,
        FileDetail:
          file?.FileDetail?.map((detail: IProductFiles) => ({
            ...detail,
            Heading: value,
          })) ?? [],
      };
    });

    setFieldValue(
      `Process.${selectedProcessIndex}.fileIndices`,
      updatedFileIndices
    );
  };
  //#endregion Local Functions

  const fileDetails = currentTab;
  const validFiles = Array.isArray(fileDetails?.FileDetail)
    ? fileDetails.FileDetail
    : [];

  const allFilesAreNull =
    fileDetails &&
    fileDetails.FileDetail?.length > 0 &&
    fileDetails.FileDetail.every(
      (f: any) =>
        !f.S3FilePath ||
        f.S3FilePath === 'null' ||
        f.S3FilePath === 'undefined'
    );

  const isPreviewPdf = useMemo(() => {
    if (values.file?.type === 'application/pdf') return true;
    if (typeof preview === 'string') {
      const path = preview.split('?')[0].toLowerCase();
      if (path.endsWith('.pdf')) return true;
    }
    const lastSel = Array.isArray(selectedImage)
      ? selectedImage[selectedImage.length - 1]
      : (selectedImage as any);
    if (lastSel && (lastSel as any).FileType === 'application/pdf') return true;
    return false;
  }, [values.file, preview, selectedImage]);

  useLayoutEffect(() => {
    const dimensionTableData = getDimensionByProcessId;

    values?.Process?.forEach((process: any) => {
      const processId = process?.ProcessId;
      const selectedProcessIndex = values?.Process?.findIndex(
        (p: any) => Number(p?.ProcessId) === Number(processId)
      );

      // Find dimensions for this process from dimensionTableData
      const processDimensions = dimensionTableData?.find(
        (d: any) => Number(d?.ProcessId) === Number(processId)
      )?.Dimensions;

      if (processDimensions?.length > 0) {
        // Process has dimensions - map them
        const selectedColumnsForProcess = extractSelectedColumnData(
          values?.Dimension,
          processDimensions?.map((d: any) => d?.Id)
        );

        const selectedColumnsArr =
          selectedColumnsForProcess?.map((e: any) => ({
            id: Number(e?.Id),
            value: Number(e?.Id),
            label: e?.Messages,
            disabled: false,
            data: e,
          })) || [];

        const seenColumns = new Set<string>();
        const dedupedSelectedColumnsArr = selectedColumnsArr.filter(
          (col: any) => {
            const key = `${Number(col?.id)}__${String(col?.label || '')
              .trim()
              .toLowerCase()}`;
            if (seenColumns.has(key)) return false;
            seenColumns.add(key);
            return true;
          }
        );

        const filteredTableData = filterTableDataBySelected({
          SelectedColumns: dedupedSelectedColumnsArr,
          TableData: values?.Dimension?.TableData || [],
        });

        setFieldValue(`Process.${selectedProcessIndex}.DimensionDetails`, {
          TableData: filteredTableData,
          SelectedColumns: dedupedSelectedColumnsArr,
        });
      } else {
        // No dimensions found for this process - set empty
        setFieldValue(`Process.${selectedProcessIndex}.DimensionDetails`, {
          TableData: [],
          SelectedColumns: [],
        });
      }
    });
  }, [getDimensionByProcessId]);

  const handleConfirmDelete = () => {
    if (!pendingDeleteData) return;
    const isTrue = (val: any) =>
      val === true || String(val).toLowerCase() === 'true';

    if (pendingDeleteData.action === 'file') {
      const {
        values,
        selectedProcessId,
        fileIndex,
        tabIndex,
        setFieldValue,
      } = pendingDeleteData;
      const selectedProcessIndex = values.Process?.findIndex(
        (p: any) => p.ProcessId === selectedProcessId
      );
      const selectedFileIndex = (
        values.Process[selectedProcessIndex]?.fileIndices ?? []
      ).findIndex((file: any) => file.TabIndex === tabIndex);

      const foundFile =
        values.Process[selectedProcessIndex].fileIndices
          ?.find((f: any) => f.TabIndex === tabIndex)
          ?.FileDetail.find((f: any, i: number) => i === fileIndex);

      const currentTabData = values.Process[selectedProcessIndex]?.fileIndices?.find(
        (file: any) => file.TabIndex === tabIndex
      );

      const { newDeletedFiles, oldDeletedFiles } = collectMatchingDeletedFiles(
        values.Process,
        foundFile,
        {
          commonOnly:
            isTrue(currentTabData?.IsDefault) || isTrue(currentTabData?.isDefault),
          tabHeading: currentTabData?.FileDetail?.[0]?.Heading,
          exactIdentityOnly: true,
        }
      );

      const mergedNewDeleted = mergeUniqueProductFiles(
        values?.NewDeletedFiles || [],
        newDeletedFiles
      );
      const mergedOldDeleted = mergeUniqueProductFiles(
        values?.OldDeletedFiles || [],
        oldDeletedFiles
      );

      setFieldValue(`NewDeletedFiles`, mergedNewDeleted);
      setFieldValue(`OldDeletedFiles`, mergedOldDeleted);

      setNewDeletedFiles(mergedNewDeleted);
      setOldDeletedFiles(mergedOldDeleted);

      if ((currentTabData?.FileDetail?.length || 0) > 1) {
        setFieldValue(
          `Process.${selectedProcessIndex}.fileIndices.${selectedFileIndex}.FileDetail`,
          [
            ...(currentTabData?.FileDetail?.filter(
              (_: any, i: number) => i !== fileIndex
            ) || []),
          ]
        );
      } else {
        removeTabAndFocus(selectedProcessIndex, tabIndex);
      }

      setShowConfirm(false);
      setPendingDeleteData(null);
      return;
    }

    const { values, selectedProcessId, productImage, setFieldValue } =
      pendingDeleteData;
    const selectedProcessIndex = values.Process?.findIndex(
      (p: any) => p.ProcessId === selectedProcessId
    );

    let foundIndices = values.Process[selectedProcessIndex].fileIndices?.find(
      (f: any) => f.TabIndex === productImage.TabIndex
    );
    const isDeletingCommon =
      isTrue(productImage.IsDefault) || isTrue((productImage as any).isDefault);

    const deletedFile = productImage.FileDetail?.[0];
    const deletedHeading = deletedFile?.Heading;
    const selectedFileId = deletedFile?.FileId;
    const targetCommonFiles = Array.isArray(productImage?.FileDetail)
      ? productImage.FileDetail
      : [];

    let oldDeletedFiles: any[] = [];
    let newDeletedFiles: any[] = [];

    // Collect files to delete from across all processes if it's a common tab
    values.Process.forEach((process: any) => {
      process.fileIndices?.forEach((fi: any) => {
        if (fi.IsDimension) return;

        let isTabMatch = false;
        const fiIsCommon =
          isTrue(fi.IsDefault) || isTrue((fi as any).isDefault);
        if (isDeletingCommon) {
          const fiHeading = fi.FileDetail?.[0]?.Heading;
          const targetHeading = productImage.FileDetail?.[0]?.Heading;
          if (fiHeading && targetHeading) {
            isTabMatch =
              fiIsCommon &&
              String(fiHeading).trim().toLowerCase() ===
                String(targetHeading).trim().toLowerCase();
          } else {
            isTabMatch =
              fiIsCommon &&
              fi.FileDetail?.some((file: any) =>
                targetCommonFiles.some((targetFile: any) =>
                  isSameProductFileReference(file, targetFile)
                )
              );
          }
        } else {
          isTabMatch =
            process.ProcessId ===
              values.Process[selectedProcessIndex]?.ProcessId &&
            fi.TabIndex === productImage.TabIndex;
        }

        if (isTabMatch) {
          fi.FileDetail?.forEach((file: any) => {
            if (file.IsNew) newDeletedFiles.push(file);
            else oldDeletedFiles.push(file);
          });
        }
      });
    });

    const uniqueOldDeleted = mergeUniqueProductFiles(oldDeletedFiles);
    const uniqueNewDeleted = mergeUniqueProductFiles(newDeletedFiles);
    const mergedNewDeleted = mergeUniqueProductFiles(
      values?.NewDeletedFiles || [],
      uniqueNewDeleted
    );
    const mergedOldDeleted = mergeUniqueProductFiles(
      values?.OldDeletedFiles || [],
      uniqueOldDeleted
    );

    setFieldValue(`NewDeletedFiles`, mergedNewDeleted);
    setFieldValue(`OldDeletedFiles`, mergedOldDeleted);

    setNewDeletedFiles(mergedNewDeleted);
    setOldDeletedFiles(mergedOldDeleted);

    if (isDeletingCommon && values.CommonProcess) {
      const updatedCommon = values.CommonProcess.filter((cp: any) => {
        const cpId = cp.FileDetail?.[0]?.FileId;
        const cpHeading = cp.FileDetail?.[0]?.Heading;
        if (
          selectedFileId &&
          Number(selectedFileId) > 0 &&
          cpId &&
          Number(cpId) > 0
        ) {
          return Number(cpId) !== Number(selectedFileId);
        }
        return cpHeading !== deletedHeading;
      });
      setFieldValue('CommonProcess', updatedCommon);
    }

    const updatedProcesses = values.Process.map((p: any) => {
      const filtered = (p.fileIndices ?? []).filter((fi: any) => {
        const fiIsCommon = isTrue(fi.IsDefault) || isTrue(fi.isDefault);
        if (fiIsCommon) {
          if (!isDeletingCommon) return true;
          const currentFileId = fi.FileDetail?.[0]?.FileId;
          const currentHeading = fi.FileDetail?.[0]?.Heading;

          if (currentHeading && deletedHeading) {
            return String(currentHeading).trim().toLowerCase() !== String(deletedHeading).trim().toLowerCase();
          }

          if (
            selectedFileId &&
            Number(selectedFileId) > 0 &&
            currentFileId &&
            Number(currentFileId) > 0
          ) {
            return Number(currentFileId) !== Number(selectedFileId);
          }
          return currentHeading !== deletedHeading;
        }
        // For non-common tabs, if it's the current process being edited, match by TabIndex
        if (p.ProcessId === values.Process[selectedProcessIndex]?.ProcessId) {
          const isMatch = fi.TabIndex === productImage.TabIndex;
          return !isMatch;
        }
        return true;
      });

      // Re-index remaining tabs for this process
      return {
        ...p,
        fileIndices: filtered.map((fi: any, idx: number) => ({
          ...fi,
          TabIndex: idx + 1,
        })),
      };
    });

    setFieldValue('Process', updatedProcesses);

    const currentProcessUpdatedIndices =
      updatedProcesses[selectedProcessIndex]?.fileIndices ?? [];

    if (currentProcessUpdatedIndices.length > 0) {
      const imageTabs = currentProcessUpdatedIndices
        .filter((fi: any) => {
          const isCommon = isTrue(fi.IsDefault) || isTrue(fi.isDefault);
          const isDimension = isTrue(fi.IsDimension) || isTrue(fi.isDimension);
          return !isCommon && !isDimension;
        })
        .sort((a: any, b: any) => a.TabIndex - b.TabIndex);

      const fallbackTabs = currentProcessUpdatedIndices
        .filter(
          (fi: any) => !(isTrue(fi.IsDimension) || isTrue(fi.isDimension))
        )
        .sort((a: any, b: any) => a.TabIndex - b.TabIndex);

      const candidates = imageTabs.length > 0 ? imageTabs : fallbackTabs;
      const nextTab =
        candidates.find((fi: any) => fi.TabIndex >= productImage.TabIndex) ||
        candidates[candidates.length - 1] ||
        currentProcessUpdatedIndices[0];

      const nextIndex = nextTab?.TabIndex ?? -1;
      setActiveIndex(nextIndex);
    } else {
      setActiveIndex(-1);
    }

    // 👇 PASTE YOUR CURRENT DELETE LOGIC HERE
    // (everything currently inside your onClick)

    setShowConfirm(false);
    setPendingDeleteData(null);
  };

  const handlePreviewFileDelete = (fileIndex: number) => {
    const selectedProcessIndex = values.Process?.findIndex(
      (p: any) => p.ProcessId === selectedProcessId
    );
    const currentTabData = values.Process[selectedProcessIndex]?.fileIndices?.find(
      (file: any) => file.TabIndex === activeIndex
    );

    const totalImages = currentTabData?.FileDetail?.length || 0;
    const fileToDelete = currentTabData?.FileDetail?.[fileIndex];

    // ─── SIDE DELETE BUTTON LOGS ─────────────────────────────────────────────
    // console.log('>>> handlePreviewFileDelete called! fileIndex=', fileIndex); // IMMEDIATE CHECK
    // console.log('=== [Side Delete Button] Image Delete Triggered ===');
    // console.log('  Deleting index  :', fileIndex, '(0-based, top=0)');
    // console.log('  Total images    :', totalImages);
    // console.log('  Direction hint  :',
    //   fileIndex === 0
    //     ? 'TOP (first image)'
    //     : fileIndex === totalImages - 1
    //     ? 'BOTTOM (last image)'
    //     : `MIDDLE (index ${fileIndex})`
    // );
    // console.log('  S3FilePath      :', fileToDelete?.S3FilePath || fileToDelete?.FilePath || '(none)');
    // console.log('  FileId          :', fileToDelete?.FileId);
    // console.log('  IsNew           :', fileToDelete?.IsNew);
    // console.log('  Heading         :', fileToDelete?.Heading);
    // console.log('  FileDetail BEFORE delete:',
    //   (currentTabData?.FileDetail || []).map((f: any, i: number) => ({
    //     index: i,
    //     FileId: f?.FileId,
    //     S3FilePath: f?.S3FilePath || f?.FilePath,
    //     IsNew: f?.IsNew,
    //   }))
    // );
    // ───────────────────────────────────────────────────────────────────────

    if (totalImages <= 1) {
      // console.log('  [Side Delete] Only 1 image – confirm dialog will remove entire tab.');
      setPendingDeleteData({
        action: 'file',
        values,
        selectedProcessId,
        fileIndex,
        tabIndex: activeIndex,
        setFieldValue,
      });
      setShowConfirm(true);
      return;
    }

    const selectedFileIndex = (
      values.Process[selectedProcessIndex]?.fileIndices ?? []
    ).findIndex((file: any) => file.TabIndex === activeIndex);

    const foundFile = currentTabData?.FileDetail?.find(
      (f: any, i: number) => i === fileIndex
    );

    const { newDeletedFiles, oldDeletedFiles } = collectMatchingDeletedFiles(
      values.Process,
      foundFile,
      {
        commonOnly:
          isTrue(currentTabData?.IsDefault) || isTrue(currentTabData?.isDefault),
        tabHeading: currentTabData?.FileDetail?.[0]?.Heading,
        exactIdentityOnly: true,
      }
    );

    const mergedNewDeleted = mergeUniqueProductFiles(
      values?.NewDeletedFiles || [],
      newDeletedFiles
    );
    const mergedOldDeleted = mergeUniqueProductFiles(
      values?.OldDeletedFiles || [],
      oldDeletedFiles
    );

    const remainingFiles = (currentTabData?.FileDetail?.filter(
      (_: any, i: number) => i !== fileIndex
    ) || []);

    // console.log('  NewDeletedFiles (after merge):', mergedNewDeleted.map((f: any) => ({ FileId: f?.FileId, S3FilePath: f?.S3FilePath || f?.FilePath })));
    // console.log('  OldDeletedFiles (after merge):', mergedOldDeleted.map((f: any) => ({ FileId: f?.FileId, S3FilePath: f?.S3FilePath || f?.FilePath })));
    // console.log('  FileDetail AFTER delete:',
    //   remainingFiles.map((f: any, i: number) => ({
    //     index: i,
    //     FileId: f?.FileId,
    //     S3FilePath: f?.S3FilePath || f?.FilePath,
    //     IsNew: f?.IsNew,
    //   }))
    // );
    // console.log('=== [Side Delete Button] End ===');

    setFieldValue(`NewDeletedFiles`, mergedNewDeleted);
    setFieldValue(`OldDeletedFiles`, mergedOldDeleted);

    setNewDeletedFiles(mergedNewDeleted);
    setOldDeletedFiles(mergedOldDeleted);

    setFieldValue(
      `Process.${selectedProcessIndex}.fileIndices.${selectedFileIndex}.FileDetail`,
      remainingFiles
    );
  };

  return (
    <div
      className="tab-content"
      id="myTabContent"
    >
      {showConfirm && (
        <ConfirmDialog
          show={showConfirm}
          title={formatMessage('delete_tab')}
          message={formatMessage("message_delete_tab_confirm")}
          onCancel={() => setShowConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
      <div
        className="tab-pane fade show active"
        id="home"
        role="tabpanel"
        aria-labelledby="home-tab"
      >
        <div className="row">
          <div className={'col-lg-12'}>
            <div
              className={`${values.Process[selectedProcessIndex]?.ViewType ? 'classVertical' : 'classHorizontal'} picture_frame step-4-frame`}
            >
              <div className="row">
                <div
                  key={`pdf_file_${selectedProcessId}_${activeIndex}`}
                  className={`${isCopyPanelOpen ? 'col-lg-6' : 'col-lg-7'}`}
                >
                  <div className="tab-view-common p-2 rounded">
                    <div className="pic_name_list">
                      {currentProcess?.fileIndices &&
                        currentProcess?.fileIndices?.length > 0 &&
                        [...(currentProcess?.fileIndices ?? [])]
                          .sort((a, b) => {
                            const isTrue = (val: any) =>
                              val === true ||
                              String(val).toLowerCase() === 'true';
                            const getPriority = (item: any) => {
                              if (
                                isTrue(item.IsDefault) ||
                                isTrue(item.isDefault)
                              )
                                return 0;
                              if (
                                isTrue(item.IsDimension) ||
                                isTrue(item.isDimension)
                              )
                                return 1;
                              return 2;
                            };
                            return getPriority(a) - getPriority(b);
                          })
                          .map((productImage: IFileIndices, index: number) => {
                            const hasDuplicateHeaderNameError =
                              hasDuplicateHeaderNameErrorForTab(
                                productImage.TabIndex
                              );
                            const hasDuplicateError =
                              hasDuplicateItemInSameHeader(
                                productImage.CheckItems
                              );
                            const hasMissingCheckItemError =
                              hasCheckItemErrors(productImage.CheckItems) ||
                              (Number(productImage.TabIndex) ===
                              Number(activeIndex)
                                ? false
                                : hasHeaderWithoutItemErrorForTab(
                                    productImage.TabIndex
                                  ));
                            const hasError =
                              hasDuplicateHeaderNameError ||
                              hasDuplicateError ||
                              hasMissingCheckItemError;
                            const tooltipMessageId = hasDuplicateHeaderNameError
                              ? 'duplicate_header_names_not_allowed'
                              : hasDuplicateError
                                ? 'duplicate_item_in_header'
                                : 'please_add_check_item';

                            return (
                              <div
                                key={productImage.TabIndex}
                                title={
                                  productImage?.FileDetail[0]?.Heading ?? ''
                                }
                                className={`textTabBlock show-Elipses ${
                                  productImage.TabIndex === activeIndex
                                    ? 'active'
                                    : ''
                                } ${activeIndex !== -1 ? '' : 'disabled-div'}`}
                                onClick={() => {
                                  handleClick(
                                    productImage.TabIndex,
                                    productImage?.FileDetail,
                                    productImage.IsDimension
                                  );
                                }}
                              >
                                <span className="pic_name">
                                  {currentProcess?.fileIndices?.length > 4 &&
                                  productImage?.FileDetail[0]?.Heading?.length >
                                    5
                                    ? productImage?.FileDetail[0]?.Heading.substring(
                                        0,
                                        5
                                      ) + '...'
                                    : productImage?.IsDimension
                                      ? formatMessage('dimension_table')
                                      : (productImage?.FileDetail[0]?.Heading ??
                                        formatMessage('image'))}
                                </span>
                                {hasError && (
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip
                                        id={`tooltip-error-tab-${index}`}
                                      >
                                        {formatMessage(tooltipMessageId)}
                                      </Tooltip>
                                    }
                                  >
                                    <ExclamationCircleFill className="text-danger ms-1" />
                                  </OverlayTrigger>
                                )}
                                {!productImage?.IsDimension &&
                                  !productImage?.IsDefault && (
                                    <span className="closeTabIocn">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="32"
                                        height="32"
                                        fill="currentColor"
                                        className="bi bi-x delete-icon"
                                        viewBox="0 0 16 16"
                                        onClick={() => {
                                          productImage?.FileDetail?.forEach((f: any) => {
                                            // console.log("Deleted FilePath (Tab Close):", f.FilePath || f.S3FilePath);
                                          });
                                          setPendingDeleteData({
                                            values,
                                            selectedProcessId,
                                            productImage,
                                            setFieldValue,
                                          });

                                          setShowConfirm(true);
                                        }}
                                      >
                                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
                                      </svg>
                                    </span>
                                  )}
                              </div>
                            );
                          })}

                      <div
                        key={imageTabLimit}
                        className={`textTabBlockAdd addImageProduct     ${isImageTabLimitReached ? 'disable-tab ' : ''} ${activeIndex > allTabCount ? 'active' : ''} `}
                        onClick={handleAddNewTab}
                      >
                        <span className="pic_name">
                          <svg
                            height="14"
                            viewBox="0 0 512 512"
                            width="14"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g
                              id="_03_Login"
                              data-name="03 Login"
                            >
                              <path d="m256 512a25 25 0 0 1 -25-25v-462a25 25 0 0 1 50 0v462a25 25 0 0 1 -25 25z" />
                              <path d="m487 281h-462a25 25 0 0 1 0-50h462a25 25 0 0 1 0 50z" />
                            </g>
                          </svg>
                        </span>
                      </div>
                      {isImageTabLimitReached && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>
                              {formatMessage('max_tabs_allowed')}
                            </Tooltip>
                          }
                        >
                          <span className="normal-info-svg">
                            <InfoCircle size={14} />
                          </span>
                        </OverlayTrigger>
                      )}
                    </div>
                    {!currentTab?.IsDimension ? (
                      <div className="copied-block-drag">
                        <div
                          className={`${
                            allTabCount < activeIndex
                              ? 'class1'
                              : 'preview-save-images-drag'
                          }`}
                        >
                          <div className="tab-view rounded">
                            <div className="row">
                              <div
                                className={`${
                                  !currentTab?.IsDefault ? 'col-5' : 'col-12'
                                }`}
                              >
                                <div className="form-group">
                                  <Form.Control
                                    type="text"
                                    placeholder={formatMessage('Heading')}
                                    name="heading"
                                    ref={headingInputRef}
                                    value={heading}
                                    maxLength={HEADING_MAX_LENGTH}
                                     isInvalid={
                                      isDuplicateHeading ||
                                      isHeadingEmptyWithFile ||
                                      String(heading || '').length >=
                                        HEADING_MAX_LENGTH
                                    }
                                    disabled={
                                      isTrue(currentTab?.IsDefault) ||
                                      isTrue(currentTab?.isDefault) ||
                                      isTrue(currentTab?.IsDimension) ||
                                      isTrue(currentTab?.isDimension)
                                    }
                                    onChange={(e) => {
                                      const val = e.target.value.slice(
                                        0,
                                        HEADING_MAX_LENGTH
                                      );
                                      setHeading(val);
                                      updateActiveProcessImageTabHeading(val);
                                      const duplicate =
                                        hasDuplicateHeadingInStep4ImageTabs(
                                          val
                                        );
                                      setIsEnable(val.length > 0 && !duplicate);
                                    }}
                                  />
                                  {isHeadingEmptyWithFile && !isDuplicateHeading && (
                                    <Form.Control.Feedback type="invalid">
                                      {formatMessage('please_enter_heading')}
                                    </Form.Control.Feedback>
                                  )}
                                  {isDuplicateHeading && (
                                    <Form.Control.Feedback type="invalid">
                                      {formatMessage('duplicate_found')}
                                    </Form.Control.Feedback>
                                  )}
                                  {!isDuplicateHeading && !isHeadingEmptyWithFile &&
                                    String(heading || '').length >=
                                      HEADING_MAX_LENGTH && (
                                      <Form.Control.Feedback type="invalid">
                                       {formatMessage('max_char_limit_256')}
                                      </Form.Control.Feedback>
                                    )}
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                  {!currentTab?.IsDefault && (
                                    <span
                                      className="ps-2 slide-copied"
                                      onClick={() =>
                                        handleCopy(
                                          currentProcess?.FileDetail?.[
                                            activeIndex
                                          ]
                                        )
                                      }
                                    >
                                      <small>
                                        {' '}
                                        {formatMessage('copy_images')}
                                      </small>{' '}
                                      <Copy />
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {previewOpen && selectedImage && (
                              <div className="image-preview-overlay">
                                <div className="images-overlay-inner">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (previewImageIndex > 0) {
                                        setPreviewImageIndex(
                                          previewImageIndex - 1
                                        );
                                      }
                                    }}
                                    className="nav-btn prev-btn"
                                  >
                                    &#10094;
                                  </button>
                                  <div className="d-flex">
                                    <div className="close-overlay-img close-btn">
                                      <span
                                        className="close-btn-icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPreviewOpen(false);
                                        }}
                                      >
                                        &times;
                                      </span>
                                    </div>
                                    <div
                                      className="close-overlay-img overlay-copy-btn close-btn"
                                      style={{ right: '60px' }}
                                      title={formatMessage('copy_images')}
                                    >
                                      <span
                                        className="close-btn-icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const imageToCopy = Array.isArray(
                                            selectedImage
                                          )
                                            ? selectedImage[previewImageIndex]
                                            : (selectedImage as any);

                                          if (imageToCopy) {
                                            setSelectedImage((prev: any) =>
                                              Array.isArray(prev)
                                                ? [...prev, imageToCopy]
                                                : [prev, imageToCopy]
                                            );
                                            handlePaste(
                                              setFieldValue,
                                              imageToCopy
                                            );
                                            setPreviewOpen(false);
                                          }
                                        }}
                                      >
                                        <Clipboard size={18} />
                                      </span>
                                    </div>
                                  </div>
                                  {Array.isArray(selectedImage) &&
                                  selectedImage.length > 0 ? (
                                    <div
                                      className="preview-item"
                                      style={{
                                        // minWidth: 240,
                                        // maxWidth: 520,
                                        flex: '0 0 auto',
                                      }}
                                    >
                                      {selectedImage[previewImageIndex]
                                        ?.FileType !== 'application/pdf' ? (
                                        <img
                                          src={
                                            selectedImage[previewImageIndex]
                                              ?.S3FilePath
                                          }
                                          draggable={false}
                                          alt={
                                            selectedImage[previewImageIndex]
                                              ?.Heading ??
                                            `preview-${previewImageIndex}`
                                          }
                                          className="full-preview-img"
                                          style={{
                                            height: 'auto',
                                            display: 'block',
                                          }}
                                        />
                                      ) : (
                                        <iframe
                                          // src={`${selectedImage[previewImageIndex]?.S3FilePath}#toolbar=0`}
                                          src={`${selectedImage[previewImageIndex]?.S3FilePath}#view=FitH&zoom=page-width&toolbar=0`}
                                          title={`PDF Viewer ${previewImageIndex}`}
                                          className="full-preview-pdf"
                                          style={{ border: 'none' }}
                                        />
                                      )}
                                    </div>
                                  ) : selectedImage &&
                                    (selectedImage as any).FileType !==
                                      'application/pdf' ? (
                                    <img
                                      src={(selectedImage as any).S3FilePath}
                                      draggable={false}
                                      alt="Full preview"
                                      className="full-preview-img"
                                    />
                                  ) : (
                                    selectedImage && (
                                      <iframe
                                        src={`${(selectedImage as any).S3FilePath}#toolbar=0`}
                                        title="PDF Viewer"
                                        className="full-preview-pdf"
                                      />
                                    )
                                  )}
                                  <button
                                    className="nav-btn next-btn"
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        previewImageIndex <
                                        (selectedImage?.length || 0) - 1
                                      ) {
                                        setPreviewImageIndex(
                                          previewImageIndex + 1
                                        );
                                      }
                                    }}
                                  >
                                    &#10095;
                                  </button>
                                </div>
                              </div>
                            )}
                            <div className="row">
                              {!values.Process?.find(
                                (p: any) => p.ProcessId === selectedProcessId
                              )?.fileIndices?.find(
                                (p: any) => p.TabIndex === activeIndex
                              )?.IsDefault && (
                                <div className="col-5">
                                  <div className="preview-img common-preview position-relative bg-white">
                                    <div className="copied-block-drag">
                                      {preview &&
                                        (!isPreviewPdf ? (
                                          <div
                                            className="drag-image relative group cursor-pointer"
                                            onClick={() =>
                                              fileInputRef.current?.click()
                                            }
                                          >
                                            <img
                                              src={preview}
                                              draggable={false}
                                              alt="chosen"
                                              className="img-thumbnail border-0 w-100 drag-product-img"
                                            />
                                            <div className="overlay-text group-hover:opacity-100">
                                              {formatMessage('drag_drop')}{' '}
                                              <a>
                                                {formatMessage('click_browse')}
                                              </a>
                                            </div>
                                          </div>
                                        ) : (
                                          <div
                                            className="drag-image relative group cursor-pointer"
                                            onClick={() =>
                                              fileInputRef.current?.click()
                                            }
                                          >
                                            <iframe
                                              src={preview}
                                              title="PDF Viewer"
                                            />
                                            <div className="overlay-text group-hover:opacity-100">
                                              {formatMessage('drag_drop')}{' '}
                                              <a>
                                                {formatMessage('click_browse')}
                                              </a>
                                            </div>
                                          </div>
                                        ))}

                                      <DragDropFileInput
                                        setFieldValue={setFieldValue}
                                        errors={errors}
                                        headingInputRef={headingInputRef}
                                        props={props}
                                        setIsEnable={setIsEnable}
                                        fileInputRef={fileInputRef}
                                        setFile={setFile}
                                        setPreview={setPreview}
                                        preview={preview}
                                        restrictedType={getTabLockedType()}
                                      />
                                    </div>
                                  </div>
                                  <div className="text-center mt-3 pt-2 save-drag-images">
                                    <Button
                                      type="button"
                                      variant="primary"
                                      onClick={() => {
                                        const selectedProcessIndex =
                                          values.Process?.findIndex(
                                            (p: any) =>
                                              p.ProcessId === selectedProcessId
                                          );
                                        if (isDuplicateHeading) {
                                          toast.error(
                                            formatMessage('duplicate_found')
                                          );
                                          return;
                                        }
                                        if (values.file && heading.length > 0) {
                                          const lockedType = getTabLockedType();
                                          const isNewPdf =
                                            values.file.type ===
                                            'application/pdf';
                                          if (
                                            lockedType &&
                                            ((lockedType === 'pdf' &&
                                              !isNewPdf) ||
                                              (lockedType === 'image' &&
                                                isNewPdf))
                                          ) {
                                            toast.error(
                                              formatMessage(
                                                lockedType === 'pdf'
                                                  ? 'invalid_pdf_file_format'
                                                  : 'supported_only_image_format'
                                              )
                                            );
                                            return;
                                          }
                                          UploadProductFileService(
                                            Number(
                                              getTenantAccessFromLocalStorage()
                                            ),
                                            {
                                              File: values.file,
                                              Heading: heading,
                                            },
                                            selectedImage[
                                              selectedImage.length - 1
                                            ],
                                            activeIndex,
                                            setFieldValue,
                                            selectedProcessId,
                                            values,
                                            selectedProcessIndex,
                                            setSelectedImageIndex,
                                            isDimensionTable
                                          );
                                          if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                          }
                                          if (headingInputRef.current) {
                                            headingInputRef.current.value = '';
                                          }
                                          setIsEnable(false);
                                          setPreview(null);
                                          setFile(null);
                                          setSelectedImage([]);
                                          setCopiedImagesUrl([]);
                                          setFieldValue('file', null);
                                          setFieldValue('selectedCopyProductImage', -1)
                                        } else if (
                                          preview &&
                                          selectedImage[
                                            selectedImage.length - 1
                                          ]?.FileId > 0 &&
                                          heading.length > 0
                                        ) {
                                          const lockedType = getTabLockedType();
                                          const isCopyPdf =
                                            selectedImage[
                                              selectedImage.length - 1
                                            ].FileType === 'application/pdf';
                                          if (
                                            lockedType &&
                                            ((lockedType === 'pdf' &&
                                              !isCopyPdf) ||
                                              (lockedType === 'image' &&
                                                isCopyPdf))
                                          ) {
                                            toast.error(
                                              formatMessage(
                                                lockedType === 'pdf'
                                                  ? 'invalid_pdf_file_format'
                                                  : 'supported_only_image_format'
                                              )
                                            );
                                            return;
                                          }
                                          UploadFileCopyService(
                                            Number(
                                              getTenantAccessFromLocalStorage()
                                            ),
                                            selectedImage[
                                              selectedImage.length - 1
                                            ],
                                            heading,
                                            activeIndex,
                                            setFieldValue,
                                            selectedProcessId,
                                            values,
                                            selectedProcessIndex,
                                            setSelectedImageIndex,
                                            isDimensionTable
                                          ).then(() => {
                                            // Keep focus on current tab after copy.
                                            // Auto-jumping to length+1 can point to a non-existent tab
                                            // and clears heading unexpectedly.
                                            setActiveIndex(activeIndex);
                                            if (fileInputRef.current) {
                                              fileInputRef.current.value = '';
                                            }
                                            if (headingInputRef.current) {
                                              headingInputRef.current.value =
                                                '';
                                            }
                                            setIsCopyPanelOpen(false)
                                            setCopiedImagesUrl([]);
                                            setFieldValue(
                                              'SelectedCopyProductImage',
                                              -1
                                            );
                                            setIsEnable(false);
                                            setPreview(null);
                                            setFile(null);
                                            setSelectedImage([]);
                                            setFieldValue('file', null);
                                          });
                                        } else {
                                          if (
                                            !heading &&
                                            heading.length === 0
                                          ) {
                                            toast.error(
                                              formatMessage(
                                                'please_enter_heading'
                                              )
                                            );
                                          } else {
                                            toast.error(
                                              formatMessage(
                                                'please_select_image'
                                              )
                                            );
                                          }
                                        }
                                      }}
                                      disabled={
                                        addProductFileState?.inProgress ||
                                        isDuplicateHeading
                                      }
                                    >
                                      {formatMessage('save')}
                                    </Button>
                                  </div>
                                </div>
                              )}
                              <div
                                className={`${
                                  !values.Process?.find(
                                    (p: any) =>
                                      p.ProcessId === selectedProcessId
                                  )?.fileIndices?.find(
                                    (p: any) => p.TabIndex === activeIndex
                                  )?.IsDefault
                                    ? 'col-7'
                                    : 'col-12'
                                }`}
                              >
                                <div
                                  className={`saved-images  process-content-4 rounded ${
                                    !values.Process?.find(
                                      (p: any) =>
                                        p.ProcessId === selectedProcessId
                                    )?.fileIndices?.find(
                                      (p: any) => p.TabIndex === activeIndex
                                    )?.IsDefault
                                      ? 'negative-margins'
                                      : ''
                                  }`}
                                >
                                  {fileDetails?.FileDetail &&
                                  fileDetails.FileDetail.length > 0 ? (
                                    allFilesAreNull ? (
                                      <img
                                        src={img404}
                                        alt={formatMessage('picture_label')}
                                      />
                                    ) : (
                                      validFiles.map(
                                        (
                                          productDetail: IProductFiles,
                                          index: number
                                        ) => {
                                          return (
                                          <div key={index}>
                                            {!productDetail.S3FilePath ? (
                                              <img
                                                src={img404}
                                                alt={formatMessage(
                                                  'picture_label'
                                                )}
                                              />
                                            ) : productDetail.FileType ===
                                              'application/pdf' ? (
                                              <div className="IframeProduct position-relative">
                                                {!fileDetails.IsDefault && (
                                                  <button
                                                    type="button"
                                                    className="btn-light-danger-new btn-light-danger images-close position-absolute rounded-circle"
                                                    onClick={() => {
                                                      // console.log(`[Side Delete - PDF] index=${index} | total=${validFiles.length} | path=${productDetail?.S3FilePath}`);
                                                      handlePreviewFileDelete(index);
                                                    }}
                                                  >
                                                    <DeleteIcon />
                                                  </button>
                                                )}
                                                {productDetail.S3FilePath &&
                                                productDetail.S3FilePath !==
                                                  'null' &&
                                                productDetail.S3FilePath !==
                                                  'undefined' ? (
                                                  <iframe
                                                    src={`${productDetail.S3FilePath}#toolbar=0`}
                                                    title="PDF Viewer"
                                                    style={{ border: 'none' }}
                                                  />
                                                ) : (
                                                  <img
                                                    src={img404}
                                                    alt={formatMessage(
                                                      'picture_label'
                                                    )}
                                                  />
                                                )}
                                              </div>
                                            ) : (
                                              <figure className="position-relative">
                                                {!fileDetails.IsDefault && (
                                                  <button
                                                    type="button"
                                                    className="btn-light-danger-new btn-light-danger images-close position-absolute rounded-circle"
                                                    onClick={() => {
                                                      // console.log(`[Side Delete - Image] index=${index} | total=${validFiles.length} | path=${productDetail?.S3FilePath}`);
                                                      handlePreviewFileDelete(index);
                                                    }}
                                                  >
                                                    <DeleteIcon />
                                                  </button>
                                                )}
                                                {productDetail.S3FilePath &&
                                                productDetail.S3FilePath !==
                                                  'null' &&
                                                productDetail.S3FilePath !==
                                                  'undefined' ? (
                                                  <img
                                                    src={
                                                      productDetail.S3FilePath
                                                    }
                                                    draggable={false}
                                                    alt={formatMessage(
                                                      'picture_label'
                                                    )}
                                                  />
                                                ) : (
                                                  <img
                                                    src={img404}
                                                    alt={formatMessage(
                                                      'picture_label'
                                                    )}
                                                  />
                                                )}
                                              </figure>
                                            )}
                                          </div>
                                          );
                                        }
                                      )
                                    )
                                  ) : (
                                    <div className="message-area preview-images-no-data">
                                      <div className="no-data-img">
                                        <img
                                          src={noMessageIcon}
                                          alt=""
                                          className="no_message"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <DimensionTableView
                        key={selectedProcessId}
                        selectedProcess={selectedProcessId}
                        id={props.id}
                        selectedImage={selectedImage}
                        selectedImageIndex={selectedImageIndex}
                      />
                    )}
                  </div>
                </div>
                <div className={isCopyPanelOpen ? 'col-lg-4' : 'col-lg-5'}>
                  <ImageTabView
                    id={id}
                    processFileDetails={values.Process?.find(
                      (p: any) => p.ProcessId === selectedProcessId
                    )}
                    processType={
                      values.Process?.find(
                        (p: any) => p.ProcessId === selectedProcessId
                      )?.Type
                    }
                    selectedProcess={selectedProcessId}
                    selectedImage={selectedImage}
                    selectedImageIndex={selectedImageIndex}
                  />
                </div>
                {isCopyPanelOpen && (
                  <div className={`col-lg-2`}>
                    <div className="d-flex justify-content-end">
                      <div
                        onClick={() => {
                          setIsCopyPanelOpen(false);
                          setIsFullWidth(true);
                          // setIsCopyPanelOpen(true);
                          // setIsCopied(false);
                          setFieldValue('SelectedCopyProductImage', -1);
                          setCopiedImagesUrl([]);
                        }}
                        className="btn btn-danger btn-sm mb-2 cursor-pointer"
                      >
                        {' '}
                        {formatMessage('close')} &times;
                      </div>
                    </div>

                    <Field
                      className=""
                      name="SelectedCopyProductImage"
                      autoComplete="off"
                      placeholder=""
                      as={FormikFormSearchSelectField}
                      value={-1}
                      disabled={false}
                      spaceAllowed={false}
                      isClearable={true}
                      options={allProductSearchSelectOptions}
                      typeofoptionid={'number'}
                      changeHandler={(option: any) => {
                        if (
                          option > 0 &&
                          option !== values.SelectedCopyProductImage
                        ) {
                          GetProductImagesByIdService(
                            Number(getTenantIdFromLocalStorage()),
                            option
                          );
                        } else {
                          setFieldValue('SelectedCopyProductImage', -1);
                          setCopiedImagesUrl([]);
                          setIsCopyPanelOpen(false)
                        }
                      }}
                    />
                    <ul className="border copy-images-block">
                      {getCopiedImagesUrl?.length > 0 ? (
                        getCopiedImagesUrl.map(
                          (copiedImage: IProductFiles, index: number) => {
                            return (
                              <li key={index}>
                                {copiedImage?.S3FilePath ? (
                                  <div className="position-relative d-inline-block images-copied-done">
                                    <div
                                      className="copy-image  top-0 end-0 rounded-circle bg-light shadow-sm"
                                      onClick={() => {
                                        setSelectedImage((prev) => [
                                          ...prev,
                                          copiedImage,
                                        ]);
                                        handlePaste(setFieldValue, copiedImage);
                                      }}
                                      title="Copy image"
                                    >
                                      <Clipboard
                                        size={18}
                                        title="copy image"
                                      />
                                    </div>
                                    <div
                                      className="images-copied-done1 cursor-pointer"
                                      onClick={() => {
                                        setSelectedImage(
                                          getCopiedImagesUrl || [copiedImage]
                                        );
                                        setPreviewImageIndex(index);
                                        setPreviewOpen(true);
                                      }}
                                    >
                                      {copiedImage?.FileType !==
                                      'application/pdf' ? (
                                        copiedImage?.S3FilePath &&
                                        copiedImage?.S3FilePath !== 'null' &&
                                        copiedImage?.S3FilePath !==
                                          'undefined' ? (
                                          <img
                                            className="border"
                                            width={100}
                                            height={100}
                                            src={copiedImage?.S3FilePath}
                                            alt="preview"
                                          />
                                        ) : (
                                          <img
                                            className="border"
                                            width={100}
                                            height={100}
                                            src={img404}
                                            alt="preview"
                                          />
                                        )
                                      ) : copiedImage?.S3FilePath &&
                                        copiedImage?.S3FilePath !== 'null' &&
                                        copiedImage?.S3FilePath !==
                                          'undefined' ? (
                                        <iframe
                                          src={`${copiedImage?.S3FilePath}#toolbar=0`}
                                          title="PDF Viewer"
                                          style={{
                                            border: 'none',
                                            width: 100,
                                            height: 100,
                                            pointerEvents: 'none',
                                          }}
                                        />
                                      ) : (
                                        <img
                                          className="border"
                                          width={100}
                                          height={100}
                                          src={img404}
                                          alt="preview"
                                        />
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <img
                                    src={img404}
                                    alt={formatMessage('picture_label')}
                                  />
                                )}
                              </li>
                            );
                          }
                        )
                      ) : (
                        <li className="messageCopied">
                          <div className="message-area">
                            <div className="no-data-img">
                              <img
                                src={noMessageIcon}
                                alt=""
                                className="no_message"
                              />
                            </div>
                            <div>
                              <p className="">
                                {formatMessage('no_copy_image')}
                              </p>
                            </div>
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ProcessTabContent;
